import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from './Button';
import '../../assets/styles/scss/components/ConfirmIconButton.scss';

/**
 * ConfirmIconButton
 *
 * A small icon-only action that requires two clicks: the first "arms" it
 * (icon + tooltip swap, with a brief pop animation so it doesn't read as an
 * instant transition), the second actually fires `onConfirm`. Auto-disarms
 * after a few seconds if the second click never comes.
 *
 * For dense list rows where a full confirmation modal would be too heavy for
 * the action's weight, but a single accidental click shouldn't be enough to
 * fire it either.
 */
const ConfirmIconButton = ({
  icon,
  confirmIcon,
  title,
  confirmTitle = 'Click again to confirm',
  variant = 'ghost',
  size = 'small',
  loading = false,
  onConfirm,
  armTimeoutMs = 3000,
}) => {
  const [armed, setArmed] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    if (!armed) {
      setArmed(true);
      timeoutRef.current = setTimeout(() => setArmed(false), armTimeoutMs);
      return;
    }
    clearTimeout(timeoutRef.current);
    setArmed(false);
    onConfirm();
  };

  return (
    <Button
      variant={armed ? 'danger' : variant}
      size={size}
      icon={armed ? (confirmIcon || icon) : icon}
      loading={loading}
      onClick={handleClick}
      title={armed ? confirmTitle : title}
      className={`confirm-icon-button${armed ? ' confirm-icon-button--armed' : ''}`}
    />
  );
};

ConfirmIconButton.propTypes = {
  icon: PropTypes.node.isRequired,
  confirmIcon: PropTypes.node,
  title: PropTypes.string,
  confirmTitle: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  armTimeoutMs: PropTypes.number,
};

export default ConfirmIconButton;
